// Minimal DAV request-body parsing (browserdav-nephele-parity Phase 3).
// Regex-based rather than a full XML parser — dependency-free and adequate
// for PROPFIND prop/allprop/propname selections and PROPPATCH
// propertyupdate bodies as real clients emit them. Namespace prefixes are
// resolved from xmlns declarations collected across the document.

function collectNamespaces(xml) {
    const namespaces = { '': 'DAV:' } // sensible default when no xmlns given
    for (const match of xml.matchAll(/xmlns(?::([\w-]+))?\s*=\s*"([^"]*)"/g)) {
        namespaces[match[1] || ''] = match[2]
    }
    return namespaces
}

function elementsIn(fragment, namespaces) {
    const found = []
    for (const match of fragment.matchAll(/<(?:([\w-]+):)?([\w-]+)((?:\s+[^<>]*?)?)\/?>/g)) {
        const [, prefix, localName] = match
        if (fragment[match.index + 1] === '/') continue // closing tag
        found.push({
            xmlns: namespaces[prefix || ''] || 'DAV:',
            name: localName,
        })
    }
    return found
}

/**
 * Parse a PROPFIND body.
 * @returns { mode: 'allprop' | 'propname' | 'prop', props?: [{xmlns, name}] }
 */
export function parsePropfindBody(body) {
    if (!body || typeof body !== 'string' || body.trim() === '') return { mode: 'allprop' }
    const xml = body.trim()
    const namespaces = collectNamespaces(xml)

    if (/<(?:[\w-]+:)?propname\s*\/?>/.test(xml)) return { mode: 'propname' }

    const propBlock = xml.match(/<(?:[\w-]+:)?prop[\s>]([\s\S]*?)<\/(?:[\w-]+:)?prop>/)
    if (propBlock) {
        const props = elementsIn(propBlock[1], namespaces)
        if (props.length > 0) return { mode: 'prop', props }
    }

    return { mode: 'allprop' } // explicit <allprop/> or unrecognized body
}

/**
 * Parse a PROPPATCH propertyupdate body.
 * @returns { sets: [{xmlns, name, value}], removes: [{xmlns, name}] }
 */
export function parseProppatchBody(body) {
    if (!body || typeof body !== 'string') throw { status: 400, message: 'PROPPATCH requires a propertyupdate body' }
    const xml = body.trim()
    const namespaces = collectNamespaces(xml)
    const sets = []
    const removes = []

    const blockRe = /<(?:[\w-]+:)?(set|remove)[\s>]([\s\S]*?)<\/(?:[\w-]+:)?\1>/g
    for (const block of xml.matchAll(blockRe)) {
        const [, kind, blockBody] = block
        const propBlock = blockBody.match(/<(?:[\w-]+:)?prop[\s>]([\s\S]*?)<\/(?:[\w-]+:)?prop>/)
        if (!propBlock) continue
        const fragment = propBlock[1]

        // Full elements with values: <pfx:name ...>value</pfx:name>
        const consumed = new Set()
        for (const el of fragment.matchAll(/<(?:([\w-]+):)?([\w-]+)((?:\s+[^<>]*?)?)>([\s\S]*?)<\/(?:[\w-]+:)?\2>/g)) {
            const [, prefix, localName, , value] = el
            const xmlns = namespaces[prefix || ''] || 'DAV:'
            consumed.add(`${prefix || ''}:${localName}`)
            if (kind === 'set') sets.push({ xmlns, name: localName, value: value.trim() })
            else removes.push({ xmlns, name: localName })
        }
        // Self-closing elements: <pfx:name/> (empty value set, or remove)
        for (const el of fragment.matchAll(/<(?:([\w-]+):)?([\w-]+)((?:\s+[^<>]*?)?)\/>/g)) {
            const [, prefix, localName] = el
            if (consumed.has(`${prefix || ''}:${localName}`)) continue
            const xmlns = namespaces[prefix || ''] || 'DAV:'
            if (kind === 'set') sets.push({ xmlns, name: localName, value: '' })
            else removes.push({ xmlns, name: localName })
        }
    }

    if (sets.length === 0 && removes.length === 0) {
        throw { status: 400, message: 'PROPPATCH body contains no property updates' }
    }
    return { sets, removes }
}
